import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Modal, Restricted } from '@/common';
import { Button, Spinner, ZeroState } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import { TEXT_OVERLAY } from '@/constants';
import { PERMISSION_TYPE } from '@/enums';
import { showToast, ToastType } from '@/helpers/toast';
import { AssetDetails } from '@/pages/Event/Asset/AssetDetails';
import { AssetItem } from '@/pages/Event/Asset/AssetItem';
import { GrayImageIcon, ImageBlueBoldIcon } from '@/resources';
import {
  useDeleteAssetMutation,
  useGetAllTextOverlaysQuery,
  useRemoveTextOverlayMutation
} from '@/services';
import { Asset, TextOverlay as TextOverlayType } from '@/types';
import { TestIds } from '@/shared/test-ids';

export const TextOverlay = () => {
  const [t] = useTranslation('common');
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [assetForPreview, setAssetForPreview] = useState<Asset | null>(null);
  const [textOverlayForRemove, setTextOverlayForRemove] = useState<TextOverlayType | null>(null);

  const {
    data: textOverlayData,
    isLoading,
    refetch: refetchTextOverlay
  } = useGetAllTextOverlaysQuery(Number(eventId));
  const { mutateAsync: removeTextOverlay, isLoading: isDeleting } = useRemoveTextOverlayMutation(
    queryClient,
    {
      onSuccess: () => {
        refetchTextOverlay();
      },
      onError: () => {
        showToast(ToastType.ERROR, t('somethingWentWrong'));
      }
    }
  );
  const { mutate: deleteAsset } = useDeleteAssetMutation(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('textOverlayDeletedSuccessfully'));
    },
    onError: () => {
      showToast(ToastType.ERROR, t('assetDeleteError'));
    }
  });

  const textOverlayItems = useMemo(() => {
    return textOverlayData?.pages.flatMap((page) => page.data?.items) || [];
  }, [textOverlayData]);

  const removeSelectedTextOverlay = async (textOverlayForRemove: TextOverlayType) => {
    await removeTextOverlay(textOverlayForRemove.id);
    await deleteAsset(textOverlayForRemove.asset.id);
    setTextOverlayForRemove(null);
  };

  const handleCreateTextOverlay = () => {
    navigate(`/events/${eventId}/text-overlay/create`);
  };

  return (
    <>
      {isLoading ? (
        <Spinner />
      ) : (
        <div
          className='flex size-full flex-col'
          data-testid={TestIds.eventTextOverlay.page}>
          <div className='mb-6 mt-4 flex flex-col md:mt-9'>
            <div className='flex md:justify-end'>
              <Restricted to={[PERMISSION_TYPE.CREATE_TEXT_OVERLAY]}>
                <Button
                  variant={ButtonVariant.SECONDARY}
                  handleOnClick={handleCreateTextOverlay}
                  className='w-full md:mr-20 md:w-fit'
                  dataTestId={TestIds.eventTextOverlay.createButton}>
                  <ImageBlueBoldIcon className='mr-2.5 md:size-4' />
                  {t('createOverlayText')}
                </Button>
              </Restricted>
            </div>
          </div>
          {textOverlayItems.length === 0 ? (
            <div
              className='mt-16 flex items-center justify-center md:mt-0 md:h-full'
              data-testid={TestIds.eventTextOverlay.zeroState}>
              <ZeroState
                icon={<GrayImageIcon />}
                titleFirstLine={t('createTextOverlayTitleOne')}
                titleSecondLine={t('createTextOverlayTitleTwo')}
                onIconClick={handleCreateTextOverlay}
              />
            </div>
          ) : (
            <div
              className='mb-10 flex-1 basis-0 overflow-y-auto scrollbar-none md:px-20'
              data-testid={TestIds.eventTextOverlay.grid}>
              <div className='grid grid-cols-2 gap-2 md:grid-cols-4'>
                {textOverlayItems.map((textOverlay, index) => (
                  <AssetItem
                    key={textOverlay.id}
                    asset={textOverlay.asset}
                    assetType={TEXT_OVERLAY}
                    canBeRemoved
                    canBeEdited={true}
                    selectedVideoUrl=''
                    editSelectedAsset={() => {
                      navigate(`/events/${eventId}/text-overlay/${textOverlay.id}`);
                    }}
                    deleteSelectedAsset={() => setTextOverlayForRemove(textOverlay)}
                    setShowAsset={() => setAssetForPreview(textOverlay.asset as Asset)}
                    dataTestId={`${TestIds.eventTextOverlay.itemCard}-${index}`}
                  />
                ))}
              </div>
            </div>
          )}
          {assetForPreview && (
            <Modal
              modalClasses='max-h-[75%] max-w-[600px] min-w-[300px] lg:min-w-[600px]'
              height='h-full'
              isOpen={true}
              label={''}
              onClose={() => setAssetForPreview(null)}>
              <AssetDetails asset={assetForPreview} />
            </Modal>
          )}
          {textOverlayForRemove && (
            <ConfirmationModal
              isOpen={!!textOverlayForRemove}
              onClose={() => setTextOverlayForRemove(null)}
              onConfirm={() => removeSelectedTextOverlay(textOverlayForRemove)}
              message={t('deleteMessage', { item: textOverlayForRemove.asset.assetName })}
              isDisabled={isDeleting}
            />
          )}
        </div>
      )}
    </>
  );
};
